import { takeEvery, takeLatest, fork, put, call, select, delay, take } from 'redux-saga/effects';
import { Webhook, WebhookPayload } from '@dev/ee-api/ee-api-models';
import { ShowToast, HideToast } from '@api/store/toast/actions';
import {
    LoadWebhooksSuccess,
    UpdateWebhookSuccess,
    DeleteWebhookSuccess,
    AddWebhookSuccess,
    WebhookError,
    SetSingleWebhook,
    SetValidateInputError,
    ClearSingleWebhook,
    LoadWebhooks,
    SetWebhookToEditSuccess,
} from '@api/store/webhooks/actions';
import { loadWebhooks, updateWebhook, deleteWebhook, addWebhook } from '@api/services/ElasticApi';
import { ApplicationState as State } from '@api/store';
import { Logger } from '@api/services/errors/appLogger';

import isURL from 'validator/lib/isURL';
import {
    ValidateWebhookInputAction,
    AddWebhookAction,
    DeleteWebhookAction,
    SetWebhookToEditAction,
    UpdateWebhookAction,
} from '@api/store/webhooks/types';

const getUserWebhooks = (state: State): Webhook[] => state.webhooks.webhooks;
const getSingleWebhook = (state: State): WebhookPayload => state.webhooks.singleWebhook;
const getWebhookErrors = (state: State): string[] => state.webhooks.errors || [];

function* fetchWebhooks(): any {
    try {
        const loadedWebhooks = yield call(loadWebhooks);
        yield put(LoadWebhooksSuccess(loadedWebhooks));
    } catch (e) {
        yield put(WebhookError());
        Logger.error(e.response.data.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* createSingleWebhook(action: AddWebhookAction): any {
    const {
        payload: { webhook },
    } = action;
    const allWebhooks = yield select(getUserWebhooks);

    const webhooksNames = allWebhooks?.map((el) => el.Name);
    try {
        if (webhooksNames?.includes(webhook.Name)) {
            throw new Error('Inbound with given name already exist.');
        }
        const addedWebhook = yield call(addWebhook, webhook);

        yield put(UpdateWebhookSuccess([...allWebhooks, addedWebhook]));
        yield put(AddWebhookSuccess());
        yield put(ClearSingleWebhook());
        yield put(
            ShowToast({
                severity: 'info',
                message: `Webhook created`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(WebhookError());
        const response = e.response?.data ? `Error:${e.response?.data?.Error} ` : e;
        Logger.error(response);
        yield put(
            ShowToast({
                severity: 'error',
                message: `${response}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* validateGivenInput(action: ValidateWebhookInputAction): any {
    const {
        payload: { name, value, type },
    } = action;

    yield delay(600);

    const singleWebhook = yield select(getSingleWebhook);
    const inputErrors = yield select(getWebhookErrors);
    let updatedErrors = new Set([...inputErrors, name]);
    if ((type === 'url' && isURL(value)) || type === 'string') {
        updatedErrors = inputErrors?.filter((el) => el !== name);
        yield put(SetSingleWebhook({ ...singleWebhook, [name]: value }));
    }

    yield put(SetValidateInputError([...updatedErrors]));
}
function* deleteSingleWebhook(action: DeleteWebhookAction): any {
    const {
        payload: { publicId },
    } = action;
    try {
        yield call(deleteWebhook, publicId);
        const allWebhooks: Webhook[] = yield select(getUserWebhooks);
        const updatedWebhooks = allWebhooks.filter((el) => el.WebhookID !== publicId);
        yield put(UpdateWebhookSuccess(updatedWebhooks));
        yield put(DeleteWebhookSuccess());
        yield put(ClearSingleWebhook());
        yield put(
            ShowToast({
                severity: 'info',
                message: `Webhook deleted`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(WebhookError());
        Logger.error(e.response.data.Error);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* setWebhookToEdit(action: SetWebhookToEditAction): any {
    const {
        payload: { publicId },
    } = action;
    let allWebhooks = yield select(getUserWebhooks);
    if (!allWebhooks) {
        yield put(LoadWebhooks());
        yield take('@@webhooks/LOAD_WEBHOOKS_SUCCESS');
    }
    allWebhooks = yield select(getUserWebhooks);
    const {
        WebhookID, //eslint-disable-line
        URL: WebNotificationUrl,
        DateCreated, //eslint-disable-line
        DateUpdated, //eslint-disable-line
        ...webhookPayloadParams
    } = allWebhooks?.filter((el) => el.WebhookID === publicId)[0];
    yield put(SetSingleWebhook({ ...webhookPayloadParams, WebNotificationUrl }));
    yield put(SetWebhookToEditSuccess());
}

function* updateSingleWebhook(action: UpdateWebhookAction): any {
    const {
        payload: { publicId, webhook },
    } = action;
    const allWebhooks: Webhook[] = yield select(getUserWebhooks);
    const webhooksNames = allWebhooks?.map((el) => el.Name);
    const webhookOriginalName = allWebhooks?.filter((el) => el.WebhookID === publicId)[0].Name;
    const updatedName = webhook.Name;
    try {
        if (webhooksNames?.includes(updatedName) && updatedName !== webhookOriginalName) {
            throw new Error('Webhook with given name already exist.');
        }
        const updatedWebhook = yield call(updateWebhook, publicId, webhook);

        const updatedWebhooks = allWebhooks.map((el) => {
            if (el.WebhookID === publicId) {
                return updatedWebhook;
            }
            return el;
        });
        yield put(UpdateWebhookSuccess(updatedWebhooks));
        yield put(ClearSingleWebhook());
        yield put(
            ShowToast({
                severity: 'info',
                message: `Webhook updated`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(WebhookError());
        const response = e.response?.data ? `Error:${e.response?.data?.Error} ` : e;
        Logger.error(response);
        yield put(
            ShowToast({
                severity: 'error',
                message: `${response}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchForLoadingWebhooks(): any {
    yield takeEvery('@@webhooks/LOAD_WEBHOOKS', fetchWebhooks);
}
function* watchForInputValidation(): any {
    yield takeLatest('@@webhooks/VALIDATE_INPUT', validateGivenInput);
}
function* watchForCreatingWebhook(): any {
    yield takeEvery('@@webhooks/ADD_WEBHOOK', createSingleWebhook);
}
function* watchForDeletetingWebhook(): any {
    yield takeEvery('@@webhooks/DELETE_WEBHOOK', deleteSingleWebhook);
}
function* watchForSettingWebhookToEdit(): any {
    yield takeEvery('@@webhooks/SET_EDITED_VALUES', setWebhookToEdit);
}
function* watchForUpdatingWebhook(): any {
    yield takeEvery('@@webhooks/UPDATE_WEBHOOK', updateSingleWebhook);
}

const webhooksSagas = [
    fork(watchForLoadingWebhooks),
    fork(watchForInputValidation),
    fork(watchForCreatingWebhook),
    fork(watchForDeletetingWebhook),
    fork(watchForSettingWebhookToEdit),
    fork(watchForUpdatingWebhook),
];

export default webhooksSagas;
